import { ArrowLeft, Menu, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Logo } from '../branding/Logo'
import { LanguageSwitcher } from '../LanguageSwitcher'

interface BlogLayoutProps {
  children: ReactNode
  showBackToBlog?: boolean
}

export function BlogLayout({ children, showBackToBlog = false }: BlogLayoutProps) {
  const { t } = useTranslation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-slate-50 font-nunito flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Logo size="md" variant="image" />
            <div className="flex flex-col leading-tight">
              <div className="flex items-center">
                <span className="text-lg font-bold" style={{ color: '#237CCA' }}>Eq</span>
                <span className="text-lg font-bold" style={{ color: '#10B981' }}>una</span>
              </div>
              <span className="text-[10px] text-slate-500 font-bold tracking-wider uppercase">
                {t('nav.brandClaim')}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-600">
            <Link to="/" className="hover:text-[#237CCA] transition-colors">
              {t('blog.nav.home')}
            </Link>
            <Link to="/blog" className="text-[#237CCA] transition-colors">
              {t('blog.nav.blog')}
            </Link>
            <Link to="/support" className="hover:text-[#237CCA] transition-colors">
              {t('blog.nav.support')}
            </Link>
            <LanguageSwitcher />
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-slate-600 hover:text-[#237CCA] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü öffnen"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="flex flex-col p-4 gap-3 text-sm font-semibold text-slate-600">
              <Link to="/" className="px-2 py-2 hover:text-[#237CCA]" onClick={() => setIsMobileMenuOpen(false)}>
                {t('blog.nav.home')}
              </Link>
              <Link to="/blog" className="px-2 py-2 text-[#237CCA]" onClick={() => setIsMobileMenuOpen(false)}>
                {t('blog.nav.blog')}
              </Link>
              <Link to="/support" className="px-2 py-2 hover:text-[#237CCA]" onClick={() => setIsMobileMenuOpen(false)}>
                {t('blog.nav.support')}
              </Link>
              <div className="pt-2 border-t border-slate-100">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Optional back-to-blog */}
      {showBackToBlog && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#237CCA] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('blog.backToOverview')}
          </Link>
        </div>
      )}

      {/* Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="py-10 mt-16 bg-slate-900 text-slate-400 text-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6 text-center md:text-left">
          <div>
            <h4 className="font-semibold text-white mb-2">Equna</h4>
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">{t('footer.columns.legal.title')}</h4>
            <ul className="space-y-1">
              <li><Link to="/datenschutz" className="hover:text-white">{t('footer.columns.legal.links.privacy')}</Link></li>
              <li><Link to="/impressum" className="hover:text-white">{t('footer.columns.legal.links.imprint')}</Link></li>
              <li><Link to="/agb" className="hover:text-white">{t('footer.columns.legal.links.terms')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">{t('footer.columns.contact.title')}</h4>
            <ul className="space-y-1">
              <li><a href="mailto:contact@equna.eu" className="hover:text-white">{t('footer.columns.contact.links.email')}</a></li>
              <li><Link to="/support" className="hover:text-white">{t('footer.columns.contact.links.support')}</Link></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  )
}
